import { Card, Flex, Grid, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import styled from "styled-components";
import SearchInput from "../media/projects/components/SearchInput";
import { Carousel, CarouselProps } from "components/Carousel/";
import { SwatchesIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { useMemo, useState } from "react";

export const ListPanel = <T,>({
  items,
  title,
  renderItems,
  getSearchText,
  carouselProps,
}: {
  items: T[];
  title: string;
  renderItems: (filteredItems: T[]) => React.ReactNode;
  getSearchText: (item: T) => string;
  carouselProps?: Partial<CarouselProps>;
}) => {
  const { theme, isMobile } = useAntTheme();
  const breakpoint = Grid.useBreakpoint();
  const t = useTranslate();

  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;

    return items?.filter((item) =>
      getSearchText(item).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [searchQuery, items, getSearchText]);

  const numberOfCardsToShow = useMemo(() => {
    if (carouselProps?.slidesToShow) return carouselProps?.slidesToShow;
    if (filteredItems?.length === 1) return 1;
    if (breakpoint.xl && filteredItems?.length > 1) return 2;
    return 1;
  }, [breakpoint, filteredItems?.length, carouselProps?.slidesToShow]);

  const rows = useMemo(() => {
    const exceedNumberOfCardsToShow =
      filteredItems?.length > numberOfCardsToShow;
    if (carouselProps?.rows && exceedNumberOfCardsToShow)
      return carouselProps?.rows;
    return breakpoint.xxl && exceedNumberOfCardsToShow ? 2 : 1;
  }, [
    breakpoint,
    filteredItems?.length,
    numberOfCardsToShow,
    carouselProps?.rows,
  ]);

  return (
    <Card style={{ width: isMobile ? "100%" : "75%", border: "none" }}>
      <Flex gap={10} align="center" justify="center">
        <StyledTitle>
          {t("pages.components.ListPanel.existingItems", { item: title })}
        </StyledTitle>
        <SearchInput
          onSearch={(value) => setSearchQuery(value)}
          value={searchQuery ?? ""}
          style={{
            width: "100%",
            color: theme.colorTextDescription,
            height: 40,
          }}
          size="large"
          placeholder={t("pages.components.ListPanel.search", {
            item: title.toLowerCase(),
          })}
          prefixStyle={{
            fontSize: 20,
          }}
        />
      </Flex>
      <Carousel
        slidesToShow={numberOfCardsToShow}
        slidesToScroll={numberOfCardsToShow}
        style={{ padding: 24, width: "100%" }}
        numberOfElements={filteredItems?.length}
        empty={{
          image: <SwatchesIcon size={100} />,
        }}
        {...carouselProps}
        rows={rows}
      >
        {renderItems(filteredItems)}
      </Carousel>
    </Card>
  );
};

export const StyledTitle = styled(Typography.Text)`
  font-size: 24px;
  line-height: 1.2;
  letter-spacing: -1.4px;
`;
